import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { NotificationTemplateTypes, Response } from '@ui/src';
import { usersApi } from '../services/users';
import { UserDetails } from '../../types';

export interface UsersColumnState {
  users?: UserDetails[];
  selectedUser?: UserDetails;
  userId?: string;
  filters: { roleId: string; search: string; onlyArchived: boolean };
  selectedRoles?: string[];
}

const initialState: UsersColumnState = {
  selectedUser: undefined,
  userId: undefined,
  filters: { roleId: 'all', search: '', onlyArchived: false },
  selectedRoles: [],
};

export const usersColumnSlice = createSlice({
  name: 'usersColumn',
  initialState,
  reducers: {
    onUserChange: (state, action: PayloadAction<string>) => {
      const selectedUser =
        state.users?.find((item) => item.id === action.payload) || undefined;

      if (selectedUser) {
        state.selectedUser = {
          ...selectedUser,
          notificationsTypes: selectedUser?.notificationsTypes?.map(
            (item) => `${item}_ACTION`,
          ) as NotificationTemplateTypes[],
        };
      }

      state.userId = action.payload;
    },
    onFilterChange: (state, action: PayloadAction<string[]>) => {
      state.selectedRoles = action.payload;
    },
    onChangeOnlyArchived: (state, action: PayloadAction<boolean>) => {
      state.filters.onlyArchived = action.payload;
    },
    onSearch: (state, action: PayloadAction<string>) => {
      state.filters.search = action.payload;
    },
    clearUsersColumnState: (state) => {
      state.users = [];
      state.selectedUser = undefined;
      state.userId = undefined;
      state.filters = initialState.filters;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      usersApi.endpoints.getUsers.matchFulfilled,
      (state, action: PayloadAction<Response<UserDetails>>) => {
        const nextUsers =
          action.payload.data.edges.map((item) => item.node) || [];

        state.users = nextUsers;

        if (nextUsers && nextUsers[0] && !state.userId) {
          if (nextUsers[0]) {
            const user = nextUsers[0];

            state.selectedUser = { ...user };
            state.userId = nextUsers[0].id;
          } else {
            state.selectedUser = undefined;
          }
        }
      },
    );
    builder.addMatcher(
      usersApi.endpoints.resetNotificationTemplateToRole.matchFulfilled,
      (state, action: PayloadAction<{ data: UserDetails }>) => {
        state.users = state.users?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.notificationTypeTemplateId =
              action.payload.data.notificationTypeTemplateId;
            result.notificationsTypes = action.payload.data.notificationsTypes;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      usersApi.endpoints.resetNotificationTemplateToUserTemplate.matchFulfilled,
      (state, action: PayloadAction<{ data: UserDetails }>) => {
        state.users = state.users?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.notificationTypeTemplateId =
              action.payload.data.notificationTypeTemplateId;
            result.notificationsTypes = action.payload.data.notificationsTypes;
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      usersApi.endpoints.editUserNotificationTypes.matchFulfilled,
      (state, action: PayloadAction<{ data: UserDetails }>) => {
        state.users = state.users?.map((item) => {
          const result = { ...item };

          if (result.id === action.payload.data.id) {
            result.notificationsTypes = action.payload.data.notificationsTypes;
          }

          return result;
        });
      },
    );
  },
});

export const {
  onUserChange,
  onFilterChange,
  clearUsersColumnState,
  onSearch,
  onChangeOnlyArchived,
} = usersColumnSlice.actions;

export default usersColumnSlice.reducer;
